// src/pages/admin/UserMonitor.tsx
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, useTheme, Drawer, CircularProgress,
  Alert, Snackbar, Button, TextField, InputAdornment, Card, Chip, Avatar,
  Stack, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Pagination, IconButton, Collapse, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Search, Close, People, PersonAdd, Visibility,
  KeyboardArrowDown, KeyboardArrowUp,
  PhoneAndroid, Web, AttachMoney, ShoppingCart,
  Smartphone, NotificationsActive,
  Receipt, Person, Public, Download, PeopleAlt,
  CheckCircle, Star,
} from '@mui/icons-material';

import Slidemenu    from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader  from '../../components/admin/adminHeader.tsx';

import {
  useUnifiedUserMonitorListQuery,
  UnifiedUserEntry,
  UnifiedUserMonitorRequest,
  UnifiedSummary,
  FilterMode,
  WebUserType,
  USER_MONITOR_ENDPOINTS,
} from '../../features/admin/userMonitorApi.ts';

// ─── CSV Download Hook ────────────────────────────────────────────────────────

function useCSVDownload(endpoint: string) {
  const [loading, setLoading] = useState(false);

  const download = async (params: Record<string, unknown>, filename: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res   = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...params, download: 1 }),
        },
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed:', err);
      alert('CSV download failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return { download, loading };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$       = (v: string | number) => `AED ${(Number(v) || 0).toFixed(2)}`;
const pct        = (n: number)          => `${(Number(n) || 0).toFixed(1)}%`;

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-AE', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return d; }
};

const getInitials = (name: string) => {
  const p = (name || '').trim().split(' ').filter(Boolean);
  return p.length >= 2
    ? `${p[0][0]}${p[1][0]}`.toUpperCase()
    : (name?.[0] || '?').toUpperCase();
};

const planColor = (planName?: string | null) => {
  const p = (planName || '').toLowerCase();
  if (p.includes('platinum')) return { bg: '#e8eaf6', color: '#3f51b5' };
  if (p.includes('gold'))     return { bg: '#fff8e1', color: '#f57f17' };
  if (p.includes('silver'))   return { bg: '#f5f5f5', color: '#616161' };
  return { bg: '#fafbfb', color: '#6d7175' };
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  icon: React.ReactNode;
  bg: string;
  value: string | number;
  label: string;
  small?: boolean;
}> = ({ icon, bg, value, label, small }) => (
  <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', height: '100%' }}>
    <Box sx={{ p: small ? 2 : 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ bgcolor: bg, borderRadius: '8px', p: small ? 0.8 : 1, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography variant={small ? 'h6' : 'h5'}
          sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.1, fontSize: small ? '16px' : undefined }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: small ? '11px' : '12px' }}>{label}</Typography>
      </Box>
    </Box>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
// User Detail Dialog (Updated - Removed Period Section)
// ─────────────────────────────────────────────────────────────────────────────

const UserDetailDialog: React.FC<{
  user: UnifiedUserEntry | null;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const [tab, setTab] = useState(0);
  if (!user) return null;

  const { profile, device, account, membership, wallet, referrals, today, period, performance, recent_orders } = user;
  const isGuest = profile.user_type === 'guest';
  const pc      = planColor(membership?.plan_name);

  const tabLabels = isGuest
    ? ['Overview', 'Performance', `Orders (${recent_orders.length})`]
    : ['Overview', 'Performance', `Orders (${recent_orders.length})`, 'Device & Profile'];

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '14px', maxHeight: '92vh' } }}>
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              width: 42, height: 42,
              bgcolor: isGuest ? '#fff3e0' : '#e3f2fd',
              color:   isGuest ? '#e65100' : '#1565c0',
              fontWeight: 700, fontSize: '16px',
            }}>
              {getInitials(profile.full_name)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>
                {profile.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isGuest ? 'Guest User' : profile.email} · #{profile.user_id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Chip size="small"
            label={isGuest ? 'Guest' : account.status_label}
            sx={{
              bgcolor: isGuest ? '#fff3e0' : account.status === 1 ? '#d1f4e0' : '#ffebee',
              color:   isGuest ? '#e65100' : account.status === 1 ? '#00783e' : '#c62828',
              fontWeight: 600, fontSize: '11px',
            }} />
          {!isGuest && membership && (
            <Chip size="small" label={membership.plan_name}
              sx={{ bgcolor: pc.bg, color: pc.color, fontWeight: 600, fontSize: '11px' }} />
          )}
          {device.app_installed && (
            <Chip size="small"
              icon={<Smartphone sx={{ fontSize: '12px !important' }} />}
              label="App Installed"
              sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '11px' }} />
          )}
          {device.push_enabled && (
            <Chip size="small"
              icon={<NotificationsActive sx={{ fontSize: '12px !important' }} />}
              label="Push Enabled"
              sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '11px' }} />
          )}
          {!isGuest && wallet && (
            <Chip size="small"
              label={`Wallet: ${fmt$(wallet.balance)}`}
              sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700, fontSize: '11px' }} />
          )}
          {account.days_since_active !== null && (
            <Chip size="small"
              label={`${account.days_since_active}d since active`}
              sx={{
                bgcolor: (account.days_since_active ?? 0) > 30 ? '#ffebee' : '#e8f5e9',
                color:   (account.days_since_active ?? 0) > 30 ? '#c62828' : '#2e7d32',
                fontWeight: 600, fontSize: '11px',
              }} />
          )}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mt: 1.5,
          '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '13px', minHeight: 40, color: '#6d7175' },
          '& .Mui-selected':      { color: '#202223', fontWeight: 600 },
          '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 2 },
        }}>
          {tabLabels.map(l => <Tab key={l} label={l} />)}
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {tab === 0 && (
          <Grid container spacing={2}>
            {/* Today Stats - Full width on mobile, half on desktop */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Today's Activity</Typography>
                <Stack spacing={0.5}>
                  {[
                    { l: 'Orders',          v: today.orders },
                    { l: 'Delivered',       v: today.delivered,       c: '#00783e' },
                    { l: 'Cancelled',       v: today.cancelled,       c: today.cancelled > 0 ? '#c62828' : undefined },
                    { l: 'Spend',           v: fmt$(today.spend),     c: '#7b1fa2' },
                    { l: 'Product Views',   v: today.product_views },
                    { l: 'Purchase Events', v: today.purchase_events },
                  ].map(({ l, v, c }) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>

            {/* Referrals - for registered users only */}
            {!isGuest && referrals && (
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Referrals</Typography>
                  <Stack spacing={0.5}>
                    {[
                      { l: 'Referral Code',   v: profile.referral_code || '—' },
                      { l: 'Total Referrals', v: referrals.total_referrals },
                      { l: 'Successful',      v: referrals.successful_referrals, c: '#00783e' },
                      { l: 'Reward Earned',   v: fmt$(referrals.total_reward_earned), c: '#7b1fa2' },
                    ].map(({ l, v, c }) => (
                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223', fontFamily: l === 'Referral Code' ? 'monospace' : undefined }}>{v}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Grid>
            )}

            {/* Membership - for registered users only */}
            {!isGuest && membership && (
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, borderRadius: '10px', border: `1px solid ${pc.color}33` }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: pc.color, display: 'block', mb: 1 }}>Membership</Typography>
                  <Stack spacing={0.5}>
                    {[
                      { l: 'Plan',      v: membership.plan_name },
                      { l: 'Subscribed',v: fmtDate(membership.subscription_date) },
                      { l: 'Expiry',    v: fmtDate(membership.expiry), c: membership.is_expired ? '#c62828' : undefined },
                      { l: 'Billing',   v: membership.recurring_label || '—' },
                    ].map(({ l, v, c }) => (
                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Grid>
            )}

            {/* Account Info - for guests only */}
            {isGuest && (
              <Grid item xs={12}>
                <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Account Info</Typography>
                  <Grid container spacing={1}>
                    {[
                      { l: 'Registered', v: account.registered_at },
                      { l: 'Last Seen',  v: account.last_active_at || '—' },
                      { l: 'Login Type', v: account.login_type },
                      { l: 'Contact',    v: profile.contact || '—' },
                    ].map(({ l, v }) => (
                      <Grid item xs={6} key={l}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{v}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 2, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="body2" color="text.secondary">Success Rate:</Typography>
                <Chip size="small" label={`${pct(performance.success_rate)}`}
                  sx={{
                    bgcolor: performance.success_rate >= 80 ? '#d1f4e0' : performance.success_rate >= 50 ? '#fff3e0' : '#ffebee',
                    color:   performance.success_rate >= 80 ? '#00783e' : performance.success_rate >= 50 ? '#e65100' : '#c62828',
                    fontWeight: 700, fontSize: '13px', height: '26px',
                  }} />
              </Box>
            </Grid>
            {[
              { label: 'All-Time Orders', items: [
                { l: 'Total Orders',     v: performance.total_orders },
                { l: 'Delivered',        v: performance.total_delivered,        c: '#00783e' },
                { l: 'Cancelled',        v: performance.total_cancelled,        c: performance.total_cancelled  > 0 ? '#c62828' : undefined },
                { l: 'Failed Delivery',  v: performance.total_failed_delivery,  c: performance.total_failed_delivery > 0 ? '#c62828' : undefined },
                { l: 'COD Orders',       v: performance.cod_orders },
                { l: 'Pay Later',        v: performance.pay_later_orders },
              ]},
              { label: 'Financials', items: [
                { l: 'Total Spend',      v: fmt$(performance.total_spend),           c: '#7b1fa2' },
                { l: 'Coupon Savings',   v: fmt$(performance.total_coupon_savings) },
                { l: 'Wallet Used',      v: fmt$(performance.total_wallet_used) },
                { l: 'Reward Discount',  v: fmt$(performance.total_reward_discount) },
                { l: 'First Order',      v: fmtDate(performance.first_order_date) },
                { l: 'Last Order',       v: fmtDate(performance.last_order_date) },
              ]},
              { label: 'Browsing', items: [
                { l: 'Total Views',      v: performance.total_views },
                { l: 'Purchase Events',  v: performance.total_purchase_events },
                { l: 'Unique Products',  v: performance.unique_products },
                { l: 'Categories',       v: performance.categories_browsed },
                { l: 'First Activity',   v: fmtDate(performance.first_activity) },
                { l: 'Last Activity',    v: fmtDate(performance.last_activity) },
              ]},
            ].map(({ label, items }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5', height: '100%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>{label}</Typography>
                  <Stack spacing={0.5}>
                    {items.map(({ l, v, c }: any) => (
                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {tab === 2 && (
          recent_orders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Receipt sx={{ fontSize: 48, color: '#c9cccf', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No recent orders</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafbfb' }}>
                    {['Order', 'Status', 'Amount', 'Payment', 'Date', 'Delivered'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent_orders.map(o => (
                    <TableRow key={o.order_id} sx={{ '&:hover': { bgcolor: '#fafbfb' }, '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>#{o.order_id}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6d7175' }}>{o.invoice_no}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Chip size="small" label={o.status}
                          sx={{
                            bgcolor: o.status_code === 7 ? '#d1f4e0' : o.status_code === 8 ? '#ffebee' : '#e3f2fd',
                            color:   o.status_code === 7 ? '#00783e' : o.status_code === 8 ? '#c62828' : '#1565c0',
                            fontWeight: 600, fontSize: '10px', height: '20px',
                          }} />
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{fmt$(o.amount)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        {o.cod         && <Chip size="small" label="COD"    sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '9px', height: '16px', mr: 0.3 }} />}
                        {o.pay_later   && <Chip size="small" label="Later"  sx={{ bgcolor: '#e8eaf6', color: '#3f51b5', fontSize: '9px', height: '16px', mr: 0.3 }} />}
                        {o.wallet_used && <Chip size="small" label="Wallet" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontSize: '9px', height: '16px' }} />}
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="caption" color="text.secondary">{o.order_date}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography variant="caption" color="text.secondary">{o.delivered_at || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {tab === 3 && !isGuest && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Account Info</Typography>
                <Stack spacing={0.8}>
                  {[
                    { l: 'Registered',    v: account.registered_at },
                    { l: 'Last Active',   v: account.last_active_at || '—' },
                    { l: 'Days Inactive', v: account.days_since_active !== null ? `${account.days_since_active} days` : '—' },
                    { l: 'Login Type',    v: account.login_type },
                    { l: 'Contact',       v: profile.contact || '—' },
                    { l: 'Country Code',  v: profile.country_code || '—' },
                  ].map(({ l, v }) => (
                    <Box key={l} sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 1 }}>Device</Typography>
                <Stack spacing={0.8}>
                  {[
                    { l: 'Device Name',  v: device.device_name  || '—' },
                    { l: 'OS Version',   v: device.os_version   || '—' },
                    { l: 'Model',        v: device.model_number || '—' },
                    { l: 'App Installed',v: device.app_installed ? 'Yes' : 'No', c: device.app_installed ? '#00783e' : '#c62828' },
                    { l: 'Push Enabled', v: device.push_enabled  ? 'Yes' : 'No', c: device.push_enabled  ? '#00783e' : '#c62828' },
                  ].map(({ l, v, c }: any) => (
                    <Box key={l} sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{l}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: c || '#202223' }}>{v}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained"
          sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Subscription Filter Component
// ─────────────────────────────────────────────────────────────────────────────

const SubscriptionFilter: React.FC<{
  filterMode: FilterMode;
  value: number | '';
  onChange: (value: number | '') => void;
}> = ({ filterMode, value, onChange }) => {
  if (filterMode === 'all') return null;
  
  return (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel sx={{ fontSize: '13px' }}>Plan</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        label="Plan"
        sx={{ borderRadius: '8px', bgcolor: 'white', fontSize: '13px' }}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value={1}>Silver</MenuItem>
        <MenuItem value={2}>Gold</MenuItem>
        <MenuItem value={3}>Platinum</MenuItem>
      </Select>
    </FormControl>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Guest Filter Component (Only for Web Mode)
// ─────────────────────────────────────────────────────────────────────────────

const GuestFilter: React.FC<{
  filterMode: FilterMode;
  value: WebUserType;
  onChange: (value: WebUserType) => void;
}> = ({ filterMode, value, onChange }) => {
  if (filterMode !== 'web') return null;
  
  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel sx={{ fontSize: '13px' }}>User Type</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as WebUserType)}
        label="User Type"
        sx={{ borderRadius: '8px', bgcolor: 'white', fontSize: '13px' }}
      >
        <MenuItem value="all">All Users</MenuItem>
        <MenuItem value="registered">Registered Only</MenuItem>
        <MenuItem value="guest">Guest Only</MenuItem>
      </Select>
    </FormControl>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Unified User Table
// ─────────────────────────────────────────────────────────────────────────────

const UnifiedUserTable: React.FC<{ filterMode: FilterMode }> = ({ filterMode }) => {
  const [search,      setSearch]      = useState('');
  const [debSearch,   setDebSearch]   = useState('');
  const [page,        setPage]        = useState(1);
  const [detail,      setDetail]      = useState<UnifiedUserEntry | null>(null);
  const [expandedId,  setExpandedId]  = useState<string | null>(null);
  const [planFilter,  setPlanFilter]  = useState<number | ''>('');
  const [guestFilter, setGuestFilter] = useState<WebUserType>('all');

  const { download: downloadCSV, loading: csvLoading } =
    useCSVDownload(USER_MONITOR_ENDPOINTS.UNIFIED_USER_MONITOR);

  // Reset when tab changes
  React.useEffect(() => { 
    setPage(1); 
    setSearch(''); 
    setDebSearch(''); 
    setExpandedId(null);
    setPlanFilter('');
    setGuestFilter('all');
  }, [filterMode]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value); 
    setPage(1);
    clearTimeout((window as any)._unifiedUserSearch);
    (window as any)._unifiedUserSearch = setTimeout(() => setDebSearch(e.target.value), 400);
  }, []);

  const query: UnifiedUserMonitorRequest = useMemo(() => {
    const q: UnifiedUserMonitorRequest = { 
      page, 
      limit: 10, 
      filter_mode: filterMode 
    };
    if (debSearch) q.search = debSearch;
    if (planFilter !== '') {
      q.plan_id = planFilter;
    }
    if (filterMode === 'web') {
      q.user_type = guestFilter;
    }
    return q;
  }, [page, debSearch, filterMode, planFilter, guestFilter]);

  const { data, isLoading, isError, refetch } = useUnifiedUserMonitorListQuery(query, {
    pollingInterval: 60000,
    refetchOnFocus:  true,
  });

  const users      = data?.data       || [];
  const summary    = data?.summary;
  const pagination = data?.pagination;
  const totalPages = pagination?.pages || 0;

  const handleDownloadCSV = () => {
    const params: Record<string, unknown> = { filter_mode: filterMode };
    if (debSearch) params.search = debSearch;
    if (planFilter !== '') params.plan_id = planFilter;
    if (filterMode === 'web') params.user_type = guestFilter;
    downloadCSV(params, `user_monitor_${filterMode}_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // ── Summary tiles ─────────────────────────────────────────────────────────
  const renderSummary = () => {
    if (!summary) return null;
    const { registered, memberships, guests, today } = summary;

    const tiles = filterMode === 'app'
      ? [
          { icon: <People sx={{ color: '#1565c0', fontSize: 20 }} />,            bg: '#e3f2fd', value: registered.total,          label: 'Total App Users' },
          { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />,       bg: '#e8f5e9', value: registered.active,          label: 'Active' },
          { icon: <Smartphone sx={{ color: '#7b1fa2', fontSize: 20 }} />,        bg: '#f3e5f5', value: registered.app_installed,   label: 'App Installed' },
          { icon: <NotificationsActive sx={{ color: '#e65100', fontSize: 20 }} />, bg: '#fff3e0', value: registered.push_enabled, label: 'Push Enabled' },
          { icon: <PersonAdd sx={{ color: '#00796b', fontSize: 20 }} />,         bg: '#e0f2f1', value: registered.new_today,       label: 'New Today' },
          { icon: <ShoppingCart sx={{ color: '#1565c0', fontSize: 20 }} />,      bg: '#e3f2fd', value: today.total_orders,         label: "Today's Orders" },
          { icon: <AttachMoney sx={{ color: '#7b1fa2', fontSize: 20 }} />,       bg: '#f3e5f5', value: fmt$(today.total_revenue),  label: "Today's Revenue" },
        ]
      : filterMode === 'web'
        ? [
            { icon: <People sx={{ color: '#1565c0', fontSize: 20 }} />,            bg: '#e3f2fd', value: registered.total,          label: 'Total Web Users' },
            { icon: <Person sx={{ color: '#e65100', fontSize: 20 }} />,            bg: '#fff3e0', value: guests.total,               label: 'Guest Users' },
            { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />,       bg: '#e8f5e9', value: registered.active,          label: 'Active Registered' },
            { icon: <PersonAdd sx={{ color: '#00796b', fontSize: 20 }} />,         bg: '#e0f2f1', value: registered.new_today,       label: 'New Today' },
            { icon: <ShoppingCart sx={{ color: '#1565c0', fontSize: 20 }} />,      bg: '#e3f2fd', value: today.total_orders,         label: "Today's Orders" },
            { icon: <AttachMoney sx={{ color: '#7b1fa2', fontSize: 20 }} />,       bg: '#f3e5f5', value: fmt$(today.total_revenue),  label: "Today's Revenue" },
          ]
        : [
            { icon: <People sx={{ color: '#1565c0', fontSize: 20 }} />,            bg: '#e3f2fd', value: registered.total,          label: 'Total Registered' },
            { icon: <Person sx={{ color: '#e65100', fontSize: 20 }} />,            bg: '#fff3e0', value: guests.total,               label: 'Guest Users' },
            { icon: <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />,       bg: '#e8f5e9', value: registered.active,          label: 'Active' },
            { icon: <Smartphone sx={{ color: '#7b1fa2', fontSize: 20 }} />,        bg: '#f3e5f5', value: registered.app_installed,   label: 'App Installed' },
            { icon: <PersonAdd sx={{ color: '#00796b', fontSize: 20 }} />,         bg: '#e0f2f1', value: registered.new_today,       label: 'New Today' },
            { icon: <ShoppingCart sx={{ color: '#1565c0', fontSize: 20 }} />,      bg: '#e3f2fd', value: today.total_orders,         label: "Today's Orders" },
            { icon: <AttachMoney sx={{ color: '#7b1fa2', fontSize: 20 }} />,       bg: '#f3e5f5', value: fmt$(today.total_revenue),  label: "Today's Revenue" },
          ];

    return (
      <>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {tiles.map(({ icon, bg, value, label }) => (
            <Grid item xs={6} sm={3} md={3} key={label}>
              <StatCard icon={icon} bg={bg} value={value} label={label} small />
            </Grid>
          ))}
        </Grid>

        {/* Membership row - only for app and web modes */}
        {filterMode !== 'all' && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#202223', mb: 1 }}>
                {filterMode === 'app' ? 'App User Subscriptions' : 'Web User Subscriptions'}
              </Typography>
            </Grid>
            {[
              { label: 'Silver',   value: memberships.silver,   color: '#616161', bg: '#f5f5f5' },
              { label: 'Gold',     value: memberships.gold,     color: '#f57f17', bg: '#fff8e1' },
              { label: 'Platinum', value: memberships.platinum, color: '#3f51b5', bg: '#e8eaf6' },
            ].map(({ label, value, color, bg }) => (
              <Grid item xs={4} sm={2.4} key={label}>
                <Card sx={{ p: 2, borderRadius: '10px', border: `1px solid ${color}33`, bgcolor: bg }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color }}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </>
    );
  };

  // ── Column definitions per filter mode (removed rating column) ─────────────
  const gridCols = filterMode === 'app'
    ? '40px 48px 1fr 1.2fr 0.8fr 100px 100px 90px 100px 70px'  // Removed rating, adjusted widths
    : filterMode === 'web'
      ? '40px 48px 80px 1fr 1.2fr 0.8fr 100px 90px 70px'        // Removed rating
      : '40px 48px 80px 1fr 1.2fr 0.8fr 100px 100px 90px 70px';  // Removed rating

  const headers = filterMode === 'app'
    ? ['', 'ID', 'Name', 'Email', 'Contact', 'Plan', 'Status', 'Wallet', 'Total Spend', 'Success %', 'Action']
    : filterMode === 'web'
      ? ['', 'ID', 'Type', 'Name', 'Email', 'Contact', 'Status', 'Total Spend', 'Success %', 'Action']
      : ['', 'ID', 'Type', 'Name', 'Email', 'Contact', 'Plan', 'Status', 'Total Spend', 'Success %', 'Action'];

  return (
    <Box>
      {/* Summary */}
      {!isLoading && !isError && renderSummary()}

      {/* Search + Filters + Export */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search name, email, contact..."
          value={search} onChange={handleSearch}
          sx={{ maxWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }}
        />
        
        <SubscriptionFilter filterMode={filterMode} value={planFilter} onChange={setPlanFilter} />
        <GuestFilter filterMode={filterMode} value={guestFilter} onChange={setGuestFilter} />
        
        <Button size="small" variant="outlined"
          startIcon={csvLoading ? <CircularProgress size={14} sx={{ color: '#202223' }} /> : <Download sx={{ fontSize: 16 }} />}
          onClick={handleDownloadCSV} disabled={csvLoading}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: '8px', borderColor: '#c9cccf', color: '#202223', bgcolor: 'white', px: 2, '&:hover': { borderColor: '#202223', bgcolor: '#f1f2f4' }, '&:disabled': { opacity: 0.6 } }}>
          {csvLoading ? 'Downloading…' : 'Export CSV'}
        </Button>
        
        {pagination && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
            {pagination.total} user{pagination.total !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={30} sx={{ color: '#1565c0' }} /></Box>}
      {isError   && <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>Failed to load user data.</Alert>}

      {/* Table */}
      {!isLoading && !isError && users.length > 0 && (
        <Box sx={{ bgcolor: 'white', borderRadius: '12px', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, gap: 1, px: 2, py: 1.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
            {headers.map(h => (
              <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase' }}>{h}</Typography>
            ))}
          </Box>

          {users.map(u => {
            const rowKey  = `${u.profile.user_type}_${u.profile.user_id}`;
            const isExp   = expandedId === rowKey;
            const isGuest = u.profile.user_type === 'guest';
            const pc      = planColor(u.membership?.plan_name);

            return (
              <Box key={rowKey}>
                <Box
                  onClick={() => setExpandedId(isExp ? null : rowKey)}
                  sx={{ display: 'grid', gridTemplateColumns: gridCols, gap: 1, px: 2, py: 1.8, alignItems: 'center', borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s', cursor: 'pointer' }}>

                  <IconButton size="small" sx={{ p: 0, color: '#6d7175' }}
                    onClick={e => { e.stopPropagation(); setExpandedId(isExp ? null : rowKey); }}>
                    {isExp ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>

                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#6d7175' }}>#{u.profile.user_id}</Typography>

                  {filterMode !== 'app' && (
                    <Chip size="small" label={isGuest ? 'Guest' : 'Reg.'}
                      sx={{ bgcolor: isGuest ? '#fff3e0' : '#e3f2fd', color: isGuest ? '#e65100' : '#1565c0', fontWeight: 700, fontSize: '10px', height: '20px', width: 'fit-content' }} />
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: isGuest ? '#fff3e0' : '#e3f2fd', color: isGuest ? '#e65100' : '#1565c0', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
                      {getInitials(u.profile.full_name)}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }} noWrap>
                      {u.profile.full_name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: '#6d7175', fontSize: '12px' }} noWrap>
                    {u.profile.email || '—'}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#6d7175', fontSize: '12px' }} noWrap>
                    {u.profile.contact || '—'}
                  </Typography>

                  {filterMode !== 'web' && (
                    <Chip size="small"
                      label={isGuest ? 'Guest' : (u.membership?.plan_name || 'None')}
                      sx={{ bgcolor: pc.bg, color: pc.color, fontWeight: 700, fontSize: '11px', height: '22px', width: 'fit-content' }} />
                  )}

                  <Chip size="small"
                    label={isGuest ? 'Guest' : u.account.status_label}
                    sx={{
                      bgcolor: isGuest || u.account.status === 1 ? '#d1f4e0' : '#ffebee',
                      color:   isGuest || u.account.status === 1 ? '#00783e' : '#c62828',
                      fontWeight: 600, fontSize: '11px', height: '22px', width: 'fit-content',
                    }} />

                  {filterMode === 'app' && (
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '12px' }}>
                      {isGuest ? '—' : fmt$(u.wallet?.balance ?? 0)}
                    </Typography>
                  )}

                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#202223', fontSize: '12px' }}>
                    {fmt$(u.performance.total_spend)}
                  </Typography>

                  <Chip size="small" label={pct(u.performance.success_rate)}
                    sx={{
                      bgcolor: u.performance.success_rate >= 80 ? '#d1f4e0' : u.performance.success_rate >= 50 ? '#fff3e0' : '#ffebee',
                      color:   u.performance.success_rate >= 80 ? '#00783e' : u.performance.success_rate >= 50 ? '#e65100' : '#c62828',
                      fontWeight: 700, fontSize: '11px', height: '22px', width: 'fit-content',
                    }} />

                  <Tooltip title="View Detail">
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setDetail(u); }}
                      sx={{ color: '#1565c0', bgcolor: '#e3f2fd', borderRadius: '6px', p: 0.6, '&:hover': { bgcolor: '#bbdefb' } }}>
                      <Visibility sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Collapse in={isExp} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2.5, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                    <Grid container spacing={1.5}>
                      {[
                        { label: 'Today', items: [
                          { l: 'Orders',    v: u.today.orders },
                          { l: 'Delivered', v: u.today.delivered,         c: '#00783e' },
                          { l: 'Spend',     v: fmt$(u.today.spend),       c: '#7b1fa2' },
                          { l: 'Views',     v: u.today.product_views },
                        ]},
                        { label: 'All-Time', items: [
                          { l: 'Orders',      v: u.performance.total_orders },
                          { l: 'Delivered',   v: u.performance.total_delivered, c: '#00783e' },
                          { l: 'Spend',       v: fmt$(u.performance.total_spend), c: '#7b1fa2' },
                          { l: 'Wallet Used', v: isGuest ? '—' : fmt$(u.performance.total_wallet_used) },
                        ]},
                        { label: 'Browsing', items: [
                          { l: 'Total Views',     v: u.performance.total_views },
                          { l: 'Purchase Events', v: u.performance.total_purchase_events },
                          { l: 'Unique Products', v: u.performance.unique_products },
                          { l: 'Categories',      v: u.performance.categories_browsed },
                        ]},
                        ...(!isGuest && u.referrals ? [{ label: 'Referrals', items: [
                          { l: 'Code',       v: u.profile.referral_code || '—' },
                          { l: 'Total',      v: u.referrals.total_referrals },
                          { l: 'Successful', v: u.referrals.successful_referrals, c: '#00783e' },
                          { l: 'Earned',     v: fmt$(u.referrals.total_reward_earned), c: '#7b1fa2' },
                        ]}] : []),
                        ...(!isGuest ? [{ label: 'Device', items: [
                          { l: 'App',    v: u.device.app_installed ? '✓ Installed' : '✗ Not Installed', c: u.device.app_installed ? '#00783e' : '#c62828' },
                          { l: 'Push',   v: u.device.push_enabled  ? '✓ Enabled'   : '✗ Disabled',     c: u.device.push_enabled  ? '#00783e' : '#c62828' },
                          { l: 'Device', v: u.device.device_name   || '—' },
                          { l: 'OS',     v: u.device.os_version    || '—' },
                        ]}] : []),
                      ].map(({ label, items }) => (
                        <Grid item xs={12} sm={6} md={3} key={label}>
                          <Card sx={{ p: 1.5, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', color: '#6d7175', display: 'block', mb: 0.8 }}>{label}</Typography>
                            <Stack spacing={0.4}>
                              {items.map(({ l, v, c }: any) => (
                                <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" noWrap>{l}</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: c || '#202223', textAlign: 'right' }}>{v}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Button size="small" startIcon={<Visibility />} onClick={() => setDetail(u)}
                      sx={{ mt: 1.5, textTransform: 'none', color: '#202223', border: '1px solid #c9cccf', borderRadius: '6px' }}>
                      Full Detail
                    </Button>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {!isLoading && !isError && users.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <People sx={{ fontSize: 56, color: '#c9cccf', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No users found</Typography>
        </Box>
      )}

      {totalPages > 1 && !isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)}
            sx={{ '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
        </Box>
      )}

      <UserDetailDialog user={detail} onClose={() => setDetail(null)} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const UserMonitor = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mainTab,    setMainTab]    = useState(0);
  const [snackbar,   setSnackbar]   = useState({
    open: false, message: '', severity: 'success' as 'success' | 'error',
  });

  const filterModes: FilterMode[] = ['all', 'app', 'web'];

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(v => !v)} />
      </Box>
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={() => setMobileOpen(v => !v)} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#202223' }}>
                  User Management / Monitor
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                  Track all users, app installs and web visitors — orders, activity & behaviour
                </Typography>
              </Box>
            </Box>

            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'visible' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{
                  px: 2,
                  '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 50, color: '#6d7175' },
                  '& .Mui-selected':      { color: '#202223', fontWeight: 700 },
                  '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 },
                }}>
                  <Tab icon={<PeopleAlt  sx={{ fontSize: 18 }} />} iconPosition="start" label="All Users" />
                  <Tab icon={<PhoneAndroid sx={{ fontSize: 18 }} />} iconPosition="start" label="App Management" />
                  <Tab icon={<Web       sx={{ fontSize: 18 }} />} iconPosition="start" label="Website Management" />
                </Tabs>
              </Box>

              <Box sx={{ p: 3 }}>
                <UnifiedUserTable filterMode={filterModes[mainTab]} />
              </Box>
            </Card>
          </Box>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserMonitor;